import React from 'react';
import {
  Dialog as MuiDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useSafeArea } from '../hooks/useSafeArea';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullWidth?: boolean;
  fullScreen?: boolean;
  scroll?: 'paper' | 'body';
}

export const Dialog: React.FC<DialogProps> = ({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = 'sm',
  fullWidth = false,
  fullScreen = false,
  scroll = 'body',
}) => {
  const { theme } = useTheme();
  const safeArea = useSafeArea();

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      scroll={scroll}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : theme.radii.large,
          backgroundColor: theme.colors.background,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: theme.colors.text.primary,
          fontWeight: 'bold',
          pb: 1,
          paddingTop: fullScreen ? `${16 + safeArea.top}px` : 2,
        }}
      >
        {title}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          paddingBottom: fullScreen ? `${24 + safeArea.bottom}px` : 2,
        }}
      >
        {children}
      </DialogContent>
      {actions && (
        <DialogActions
          sx={{
            px: 3,
            pb: fullScreen ? `${16 + safeArea.bottom}px` : 2,
          }}
        >
          {actions}
        </DialogActions>
      )}
    </MuiDialog>
  );
};
